import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatomoTracker } from 'ngx-matomo-client';
import { BehaviorSubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
declare global {
  interface Window {
    dataLayer: Array<any>;
    gtag: (...args: any[]) => void;
  }
}
declare var gtag: Function;
type Rights = {
  save_preferences: boolean;
  save_filters: boolean;
  data_consent: boolean;
  google_analytics: boolean;
  data_protection: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class CookieService implements OnDestroy {
  public cookieRights?: Rights;
  public neededRightsChange?: Rights;
  private matomoTrackingSub?: Subscription;
  
  // Observable for consent changes
  private consentChangeSubject = new BehaviorSubject<Rights | undefined>(undefined);
  public consentChanges$ = this.consentChangeSubject.asObservable();
  
  // Track if user just confirmed their choice (temporary state)
  private justConfirmed = false;

  constructor(private router: Router, private readonly tracker: MatomoTracker) {
    this.updateTracker();

    if (this.rights?.google_analytics) {
      this.loadGoogleTagManager();
    }
    this.initMatomoTracking();
    
    // Reset justConfirmed state on page load
    this.justConfirmed = false;
  }

  private initMatomoTracking() {
    // Beende ggf. alte Subscription
    if (this.matomoTrackingSub) {
      this.matomoTrackingSub.unsubscribe();
    }
    // Nur abonnieren, wenn Zustimmung vorliegt
    if (this.rights?.data_consent) {
      this.matomoTrackingSub = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.tracker.trackPageView();
        }
      });
    }
  }

  public loadGoogleTagManager() {
    this.onRouteChange();

    // dynamically add analytics scripts to document head
    try {
      const url = 'https://www.googletagmanager.com/gtag/js?id=';
      const gTagScript = document.createElement('script');
      gTagScript.async = true;
      gTagScript.src = `${url}${environment.googleAnalyticsId}`;
      document.head.appendChild(gTagScript);

      const dataLayerScript = document.createElement('script');
      dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${environment.googleAnalyticsId}', {'send_page_view': false});`;
      document.head.appendChild(dataLayerScript);
    } catch (e) {
      console.error('Error adding Google Analytics', e);
    }
  }

  private onRouteChange() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        gtag('config', environment.googleAnalyticsId, {
          page_path: event.urlAfterRedirects,
        });
      }
    });
  }

  public event(action: string, eventCategory?: string, eventLabel?: string, value?: string) {
    gtag('event', action, {
      ...(eventCategory && { event_category: eventCategory }),
      ...(eventLabel && { event_label: eventLabel }),
      ...(value && { value: value }),
    });
  }

  updateTracker(): void {
    if (this.rights?.data_consent) {
      this.tracker.rememberConsentGiven();
      this.tracker.forgetUserOptOut();
      this.initMatomoTracking(); // Tracking aktivieren
    } else {
      this.tracker.forgetConsentGiven();
      this.tracker.optUserOut();
      if (this.matomoTrackingSub) {
        this.matomoTrackingSub.unsubscribe(); // Tracking deaktivieren
        this.matomoTrackingSub = undefined;
      }
    }
  }

  /**
   * Updates chatbot consent based on data protection rights
   * This method should be called when data protection consent changes
   */
  updateChatbotConsent(): void {
    // Notify subscribers about consent changes
    this.consentChangeSubject.next(this.rights);
  }

  allowSingleRight(key: keyof Rights) {
    if (this.rights) {
      this.rights[key] = true;
    } else {
      const tmp = {
        save_preferences: false,
        save_filters: false,
        data_consent: false,
        google_analytics: false,
        data_protection: false,
      };
      tmp[key] = true;
      this.rights = tmp;
    }
  }

  get changeOfRigthsNeeded(): boolean {
    // Only return true if we have explicit needed rights change (for detailed banner)
    if (this.neededRightsChange) {
      return (
        !!this.neededRightsChange.data_consent ||
        !!this.neededRightsChange.save_filters ||
        !!this.neededRightsChange.save_preferences ||
        !!this.neededRightsChange.google_analytics ||
        !!this.neededRightsChange.data_protection
      );
    }
    
    return false;
  }

  get showSimpleBanner(): boolean {
    // Don't show banner if user just confirmed their choice (temporary state)
    if (this.justConfirmed) {
      return false;
    }

    // Show banner only when no stored consent exists.
    // Users may legitimately opt out of specific categories; that must not
    // cause the banner to reappear on every refresh.
    return !this.rights;
  }
  
  // Method to set temporary confirmation state
  setJustConfirmed(value: boolean): void {
    this.justConfirmed = value;
  }
  get rights() {
    if (!this.cookieRights) {
      const raw = this.getCookie('rights');
      if (raw) {
        try {
          this.cookieRights = JSON.parse(raw);
        } catch {
          // Corrupt cookie would throw here and break DI / bootstrap (blank page).
          this.cookieRights = undefined;
          this.deleteCookie('rights');
        }
      }
    }
    return this.cookieRights;
  }

  set rights(r: Rights | undefined) {
    this.cookieRights = r;
    if (r) {
      this.setCookie({
        expireDays: 365,
        name: 'rights',
        value: JSON.stringify(r),
      });

      // Lade GTM-Skript nur bei Zustimmung für google_analytics
      if (r.google_analytics) {
        this.loadGoogleTagManager();
      }
      
      // Notify about consent changes
      this.consentChangeSubject.next(r);
    } else {
      this.deleteCookie('rights');
    }
    this.updateTracker();
  }

  public getCookie(name: string) {
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  public deleteCookie(cookieName: string) {
    this.setCookie({ name: cookieName, value: '', expireDays: -1 });
  }
  public setCookie(params: {
    expireDays: number;
    name: string;
    value: any;
    session?: boolean;
    path?: string;
    secure?: boolean;
  }) {
    let d: Date = new Date();
    d.setTime(
      d.getTime() +
      (params.expireDays ? params.expireDays : 1) * 24 * 60 * 60 * 1000
    );
    document.cookie =
      (params.name ? params.name : '') +
      '=' +
      (params.value ? params.value : '') +
      ';' +
      (params.session && params.session == true
        ? ''
        : 'expires=' + d.toUTCString() + ';') +
      'path=' +
      (params.path && params.path.length > 0 ? params.path : '/') +
      ';' +
      (location.protocol === 'https:' && params.secure && params.secure == true
        ? 'secure'
        : '');
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.matomoTrackingSub) {
      this.matomoTrackingSub.unsubscribe();
    }
    
    // Complete the BehaviorSubject to prevent memory leaks
    this.consentChangeSubject.complete();
  }
}
